<script lang="ts" setup>
import type WorkTag from '@sysarb/sysarb-api/models/entities/WorkTag';
import { TagState } from '@sysarb/sysarb-api/models/entities/WorkTag';
import type MappingEmploymentTag from '@sysarb/sysarb-api/models/entities/MappingEmploymentTag';
import debounce from 'debounce';
import type MappingWorkEqualWorksTag from '@sysarb/sysarb-api/models/entities/MappingWorkEqualWorksTag';
import type { PropType} from 'vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type MappingWorkConnectedWorksTag from '@sysarb/sysarb-api/models/entities/MappingWorkConnectedWorksTag';
import { useWorkTagsHelper } from '@/common/WorkTagsHelper';

type EmploymentTagStates = {
    positive: number;
    negative: number;
    neutral: number;
    noState: number;
    total: number;
};

const { t } = useI18n();
const workTagsHelper = useWorkTagsHelper();

const props = defineProps({
    mappingEmploymentTags: {
        type: Array as PropType<MappingEmploymentTag[]>,
        default: null
    },
    selectedWorkTags: {
        type: Array as PropType<(MappingWorkEqualWorksTag | MappingWorkConnectedWorksTag | MappingEmploymentTag)[]>,
        default: () => []
    },
    workTag: {
        type: Object as PropType<WorkTag>,
        default: null
    },
    readonly: {
        type: Boolean,
        default: false
    },
    disableNeutralState: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits<{
    'changeState': [workTag: WorkTag, tagState: TagState | null];
}>();

const employmentStateSummary = ref<EmploymentTagStates | null>(null);

const currentTagState = computed((): TagState | null => {
    if (props.selectedWorkTags != null && props.workTag == null) return null;
    if (props.workTag.id == null) return null;
    const workTagId = props.workTag.id;
    const currentTag = props.selectedWorkTags.find(x => x.tagID?.id?.isEqualTo(workTagId) === true);
    if (currentTag == null) return null;
    return currentTag.tagState.value;
});

const tagName = computed((): string => {
    if (props.workTag == null) return '';
    return workTagsHelper.getTag(props.workTag);
});

const selected = computed((): boolean | null => {
    if (props.selectedWorkTags != null && props.workTag == null) return null;
    if (props.workTag.id == null) return null;
    const workTagId = props.workTag.id;
    return props.selectedWorkTags.some(x => x.tagID?.id?.isEqualTo(workTagId) === true);
});

function setEmploymentTagsStateCount(): void {
    if (props.mappingEmploymentTags == null || props.workTag == null) return;
    if (props.workTag.id == null) return;
    const workTagId = props.workTag.id;
    // Filter all tags that match current WorkTag
    const employmentTags = props.mappingEmploymentTags.filter(x => x.tagID?.id?.isEqualTo(workTagId) === true);
    // Reset employment tag states.
    employmentStateSummary.value = {
        positive: 0,
        negative: 0,
        neutral: 0,
        noState: 0,
        total: 0,
    };

    // Calculate all tagstates that are currently selected
    employmentTags.forEach(tag => {
        if (employmentStateSummary.value == null) return;

        switch (tag.tagState.value) {
            case TagState.positive:
                employmentStateSummary.value.positive++;
                break;
            case TagState.negative:
                employmentStateSummary.value.negative++;
                break;
            case TagState.neutral:
                employmentStateSummary.value.neutral++;
                break;
            case TagState.noState:
                employmentStateSummary.value.noState++;
                break;
            default:
                break;
        }
    });

    if (employmentStateSummary.value == null) return;
    employmentStateSummary.value.total = employmentTags?.length ?? 0;
}

function getMappingWorkTagsTranslation(val: string, count: number): string {
    switch (val) {
        case 'addAutoText':
            return t('components.mappingWorkTags.addAutoText', count);
        case 'employeeHasBeenTagged':
            return t('components.mappingWorkTags.employeeHasBeenTagged', count);
        case 'employmentTagState':
            return t('components.mappingWorkTags.employmentTagState', count);
        case 'high':
            return t('components.mappingWorkTags.high', count);
        case 'less':
            return t('components.mappingWorkTags.less', count);
        case 'low':
            return t('components.mappingWorkTags.low', count);
        case 'markedEmployees':
            return t('components.mappingWorkTags.markedEmployees', count);
        case 'more':
            return t('components.mappingWorkTags.more', count);
        case 'negative':
            return t('components.mappingWorkTags.negative', count);
        case 'negativeTooltip':
            return t('components.mappingWorkTags.negativeTooltip', count);
        case 'neutral':
            return t('components.mappingWorkTags.neutral', count);
        case 'neutralTooltip':
            return t('components.mappingWorkTags.neutralTooltip', count);
        case 'noTags':
            return t('components.mappingWorkTags.noTags', count);
        case 'notPossibleToUnmarkTag':
            return t('components.mappingWorkTags.notPossibleToUnmarkTag', count);
        case 'person':
            return t('components.mappingWorkTags.person', count);
        case 'persons':
            return t('components.mappingWorkTags.persons', count);
        case 'positive':
            return t('components.mappingWorkTags.positive', count);
        case 'positiveTooltip':
            return t('components.mappingWorkTags.positiveTooltip', count);
        case 'tag':
            return t('components.mappingWorkTags.tag', count);
        case 'tagHeaderIndividual':
            return t('components.mappingWorkTags.tagHeaderIndividual', count);
        case 'tagHeaderMarket':
            return t('components.mappingWorkTags.tagHeaderMarket', count);
        case 'tagHeaderWork':
            return t('components.mappingWorkTags.tagHeaderWork', count);
        case 'tagsReadonly':
            return t('components.mappingWorkTags.tagsReadonly', count);
        default:
            return '';
    }
}

/**
 * Creates a string to be used inside of tooltip when displaying different employment states
 * @param tagCount tagState amount.
 * @param translationString string to get translation for the tagtype.
 */
function getEmploymentStateTooltipString(tagCount: number, translationString: string): string {
    return t('components.mappingWorkTags.employmentTagState', { count: tagCount, noun: tagCount > 1 ? t('components.mappingWorkTags.persons') : t('components.mappingWorkTags.person'), tagType: getMappingWorkTagsTranslation(translationString, tagCount) });
}

function getTagStateTooltip(state: TagState): string {
    if (props.workTag == null) return '';

    const format = {
        high: props.workTag.tagEn === 'Performance' ? t('components.mappingWorkTags.high') : t('components.mappingWorkTags.more'),
        low: props.workTag.tagEn === 'Performance' ? t('components.mappingWorkTags.low') : t('components.mappingWorkTags.less'),
        tagName: workTagsHelper.getTag(props.workTag)
    };

    switch (state) {
        case TagState.positive:
            return t('components.mappingWorkTags.positiveTooltip', format);
        case TagState.negative:
            return t('components.mappingWorkTags.negativeTooltip', format);
        case TagState.neutral:
            return t('components.mappingWorkTags.neutralTooltip', format);
        case TagState.noState:
            return t('components.mappingWorkTags.noStateTooltip', format);
            break;
        default:
            return '';
    }
}

/**
 * Function will emit and update, create or delete choosen tag depending on it's state.
 * @param tagState Tagstate that we want to update the tag with.
 * @param toggleWithAnalyzeText Boolean to check if we want to update the analyze text aswell.
 */
function toggleTagState(tagState: TagState | null): void {
    emit('changeState', props.workTag, tagState);
}

const employmentStateTooltip = computed((): string | null => {
    if (employmentStateSummary.value == null) return null;
    if (employmentStateSummary.value.noState > 0) {
        return '';
    }
    let lastTagLength = 0;
    let tooltipString = t('components.mappingWorkTags.employeeHasBeenTagged');
    if (employmentStateSummary.value.positive > 0) {
        tooltipString += getEmploymentStateTooltipString(employmentStateSummary.value.positive, 'positive');
        lastTagLength = employmentStateSummary.value.positive;
    }
    if (employmentStateSummary.value.neutral > 0) {
        if (employmentStateSummary.value.positive > 0) tooltipString += ', ';
        tooltipString += getEmploymentStateTooltipString(employmentStateSummary.value.neutral, 'neutral');
        lastTagLength = employmentStateSummary.value.neutral;
    }
    if (employmentStateSummary.value.negative > 0) {
        if (employmentStateSummary.value.neutral > 0
            || employmentStateSummary.value.positive > 0) tooltipString += ', ';
        tooltipString += getEmploymentStateTooltipString(employmentStateSummary.value.negative, 'negative');
        lastTagLength = employmentStateSummary.value.negative;
    }
    tooltipString += ` ${t('components.mappingWorkTags.tag', lastTagLength)}`;
    return tooltipString;
});

function initTag(): void {
    if (props.workTag == null) return;
    setEmploymentTagsStateCount();
}

const debounceInitTag = debounce(initTag, 100);

watch([
    (): MappingEmploymentTag[] => props.mappingEmploymentTags,
    (): (MappingWorkEqualWorksTag | MappingWorkConnectedWorksTag | MappingEmploymentTag)[] => props.selectedWorkTags
], () => {
    debounceInitTag();
});

debounceInitTag();
</script>

<template>
    <div v-if="(!readonly && !workTag.isDeprecated) || ((!readonly && workTag.isDeprecated) && (employmentStateSummary != null && employmentStateSummary.total > 0 || selected)) " class="position-relative">
        <!-- Tag with employmentstates -->
        <span
            v-if="employmentStateSummary != null && employmentStateSummary.total > 0"
            v-tooltip.bottom="$t('components.mappingWorkTags.notPossibleToUnmarkTag', { tagStatesString: employmentStateTooltip })"
        >
            <button
                :disabled="true"
                :class="['flex-container align-middle align-justify padding-vertical-3 padding-horizontal-5 margin-bottom-5 sy-worktag', {'selected': employmentStateSummary != null && employmentStateSummary.total > 0}]"
            >
                <span>{{ tagName }}</span>
                <span v-if="employmentStateSummary != null" class="flex-container align-middle sy-type-standard">
                    <Icon :icon="employmentStateSummary.total > 1 ? 'users' : 'user'" class="sy-icon-small margin-right-3" />
                    {{ employmentStateSummary.total }}
                </span>
            </button>
        </span>

        <!-- Tag with states Can be toggled on or of or one of three states: positive, neutral or negative. -->
        <button
            v-else-if="workTag.hasVariableState"
            :class="['flex-container margin-bottom-5 sy-worktag tagstates', {'selected': selected}]"
        >
            <!-- Tag title. -->
            <span class="sy-worktag-title">{{ tagName }}</span>
            <!-- selected tagstate icon -->
            <Icon
                :icon="currentTagState === TagState.neutral ? 'equal' : currentTagState === TagState.positive ? 'arrow-up-right' : currentTagState === TagState.negative ? 'arrow-down-right' : null"
                class="sy-icon-standard sy-active-tagstate-icon"
            />
            <!-- Tag positive button. -->
            <div class="sy-worktag-state-container">
                <button
                    v-tooltip.bottom="{ content: getTagStateTooltip(TagState.negative), delay: { show: 1000, hide: 0 }}"
                    :class="['sy-worktag-state-button', {'sy-tag-selected' : currentTagState === TagState.negative}]"
                    @click="toggleTagState(currentTagState === TagState.negative ? null : TagState.negative)"
                >
                    <Icon icon="arrow-down-right" :class="['sy-icon-standard']" />
                </button>
                <!-- Tag neutral button. -->
                <button
                    v-if="!disableNeutralState"
                    v-tooltip.bottom="{ content: getTagStateTooltip(TagState.neutral), delay: { show: 1000, hide: 0 }}"
                    :class="['sy-worktag-state-button', {'sy-tag-selected' : currentTagState === TagState.neutral}]"
                    @click="toggleTagState(currentTagState === TagState.neutral ? null : TagState.neutral)"
                >
                    <Icon icon="equal" :class="['sy-icon-standard']" />
                </button>
                <!-- Tag negative button. -->
                <button
                    v-tooltip.bottom="{ content: getTagStateTooltip(TagState.positive), delay: { show: 1000, hide: 0 }}"
                    :class="['sy-worktag-state-button', {'sy-tag-selected' : currentTagState === TagState.positive}]"
                    @click="toggleTagState(currentTagState === TagState.positive ? null : TagState.positive)"
                >
                    <Icon icon="arrow-up-right" :class="['sy-icon-standard']" />
                </button>
            </div>
        </button>

        <!-- Tag without states Will be toggled either on or off. -->
        <button
            v-else
            :class="['flex-container align-middle padding-vertical-3 padding-horizontal-5 margin-bottom-5 sy-worktag', {'selected': selected}]"
            class="flex-container align-middle padding-vertical-3 padding-horizontal-5 sy-worktag"
            @click="toggleTagState(TagState.noState)"
        >
            <span class="sy-tag-name sy-truncate">{{ tagName }}</span>
        </button>
    </div>
    <!-- Readonly tag -->
    <button
        v-else-if="employmentStateSummary != null && employmentStateSummary.total > 0 || selected"
        :disabled="true"
        class="flex-container align-middle align-justify padding-vertical-3 padding-horizontal-5 margin-bottom-3 sy-worktag selected"
    >
        <span class="sy-truncate">{{ tagName }}</span>
        <span v-if="employmentStateSummary != null && employmentStateSummary.total > 0" class="inline-flex align-middle sy-type-standard">
            <Icon :icon="employmentStateSummary.total > 1 ? 'users' : 'user'" class="sy-icon-standard margin-left-3" />
            {{ employmentStateSummary.total }}
        </span>
        <template v-if="workTag.hasVariableState">
            <Icon v-if="currentTagState === TagState.positive" icon="arrow-up-right" class="sy-icon-standard sy-icon-blue margin-left-3" />
            <Icon v-if="currentTagState === TagState.neutral" icon="pause" class="sy-icon-rotate-90 sy-icon-standard sy-icon-blue margin-left-3" />
            <Icon v-if="currentTagState === TagState.negative" icon="arrow-down-right" class="sy-icon-standard sy-icon-blue margin-left-3" />
        </template>
    </button>
</template>

<style scoped lang="scss">
.sy-worktag {
    font-weight: var(--sy-font-weight-medium);
    font-size: var(--sy-font-size-standard);
    color: var(--sy-gray-700);
    background-color: var(--sy-gray-100);
    border-radius: var(--sy-border-radius-lg);
    position: relative;
    width: 100%;
    height: 40px;

    &.selected {
        color: var(--sy-blue-600);
        background: var(--sy-blue-100);
    }

    &:hover {
        background-color: var(--sy-gray-200);
        color: var(--sy-gray-900);
    }

    &.tagstates {
        &:hover {
            background-color: var(--sy-white);
            > .sy-worktag-title {
                top: -12px;
                left: 8px;
                padding: 2px 4px;
                border-radius: var(--sy-border-radius-lg);
                background: var(--sy-white);
                color: var(--sy-gray-900);
                font-size: var(--sy-font-size-small);
                margin: 0 auto;
                transition: all 0.4s ease;
            }
            > .sy-worktag-state-container > .sy-worktag-state-button {
                opacity: 1;
                transition: all 0.4s ease;
            }
            > .sy-active-tagstate-icon {
                display: none;
            }
        }
    }
}

.sy-worktag-state-container {
    border-radius: var(--sy-border-radius-lg);
    display: flex;
    width: 100%;
    height: 100%;

    & > .sy-worktag-state-button {
        &:first-child {
            border-radius: var(--sy-border-radius-lg) 0 0 var(--sy-border-radius-lg);
        }

        &:last-child {
            border-radius: 0 var(--sy-border-radius-lg) var(--sy-border-radius-lg) 0;
        }
    }
}
.sy-active-tagstate-icon {
    border-radius: none;
    display: block;
    position: absolute;
    top: 10px;
    right: 16px;
    transition: all 0.4s ease;
}

.sy-worktag-title {
    position: absolute;
    top: 12px;
    left: 16px;
    transition: all 0.4s ease;
}

.sy-worktag-state-button {
    width: 100%;
    height: 100%;
    opacity: 0;
    background-color: var(--sy-gray-100);
    color: var(--sy-gray-900);
    &:hover {
        background-color: var(--sy-gray-200);
        color: var(--sy-gray-900);
    }
    &.sy-tag-selected {
        background-color: var(--sy-blue-100);
        color: var(--sy-blue-600);
    }
}

.sy-tag-name {
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
